<template>
  <Modal ref="modal" @on-show-update="onModalShowUpdate">
    <template #header>
      <slot />
    </template>

    <Form ref="form" :rules="rules" :form-value="formValue">
      <template v-if="step === 1">
        <n-form-item path="data.email" :label-props="{ for: 'email' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="Email"
              :is-required="true"
              demo-tooltip-text="Insert email address (i.e. carrier@email.com)"
              @on-demo-click="fillExampleEmail"
            />
          </template>
          <n-input
            v-model:value="formValue.data.email"
            placeholder="email@example.com"
            :input-props="{ id: 'email' }"
          />
        </n-form-item>

        <n-form-item path="data.businessName" :label-props="{ for: 'businessName' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="Business name"
              demo-tooltip-text="Insert business name (i.e. AlphaTheta)"
              @on-demo-click="fillExampleBusinessName"
            />
          </template>
          <n-input
            v-model:value="formValue.data.businessName"
            placeholder="Company business name"
            :input-props="{ id: 'businessName' }"
          />
        </n-form-item>

        <DefaultCommissionFormItem :value="formValue.data.defaultCommission" @update="onCommissionInputUpdate" />
      </template>

      <template v-if="step === 2">
        <n-form-item path="data.address.address1" :label-props="{ for: 'address1' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="Address 1"
              :is-required="true"
              demo-tooltip-text="Insert address (i.e. 123 Pineapple St)"
              @on-demo-click="fillExampleAddress1"
            />
          </template>
          <n-input
            v-model:value="formValue.data.address.address1"
            placeholder="Address 1"
            :input-props="{ id: 'address1' }"
          />
        </n-form-item>

        <n-form-item label="Address 2" path="data.address.address2" :label-props="{ for: 'address2' }">
          <n-input
            v-model:value="formValue.data.address.address2"
            placeholder="Address 2"
            :input-props="{ id: 'address2' }"
          />
        </n-form-item>

        <n-form-item path="data.address.city" :label-props="{ for: 'city' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="City"
              :is-required="true"
              demo-tooltip-text="Insert city (i.e. Philadelphia)"
              @on-demo-click="fillExampleCity"
            />
          </template>
          <n-input v-model:value="formValue.data.address.city" placeholder="City" :input-props="{ id: 'city' }" />
        </n-form-item>

        <n-form-item path="data.address.state" :label-props="{ for: 'state' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="State"
              :is-required="true"
              demo-tooltip-text="Insert state (i.e. PA)"
              @on-demo-click="fillExampleState"
            />
          </template>
          <n-select
            v-model:value="formValue.data.address.state"
            :options="stateSelectOptions"
            :input-props="{ id: 'state' }"
            filterable
          />
        </n-form-item>

        <n-form-item path="data.address.postalCode" :label-props="{ for: 'postalCode' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="Postal code"
              :is-required="true"
              demo-tooltip-text="Insert postal code (i.e. 19123)"
              @on-demo-click="fillExamplePostalCode"
            />
          </template>
          <n-input
            v-model:value="formValue.data.address.postalCode"
            placeholder="12345"
            :input-props="{ id: 'postalCode' }"
          />
        </n-form-item>

        <n-form-item
          path="data.address.phone"
          :label-props="{ for: 'phone' }"
          :validation-status="phoneValidationStatus"
          :feedback="phoneFeedback"
          :show-require-mark="false"
        >
          <template #label>
            <FormLabel
              label="Phone"
              demo-tooltip-text="Insert phone number (i.e. +12673212345)"
              @on-demo-click="fillExamplePhone"
            />
          </template>
          <VueTelInput v-model="formValue.data.address.phone" @validate="setPhoneValidity" />
        </n-form-item>
      </template>

      <template v-else-if="step === 3 && bankControlStates.showControls">
        <div class="sr-carrier-form-bank-control-header">
          <template v-if="bankControlStates.addBank">
            <h2>The new bank will replace the existing one</h2>
          </template>
          <template v-else-if="bankControlStates.deleteBank">
            <h2>Bank will be deleted</h2>
          </template>
          <template v-else>
            <h2>Bank already exists</h2>
          </template>
        </div>
        <div class="sr-carrier-form-bank-control-actions">
          <n-button-group>
            <n-button
              :disabled="bankControlStates.addBank || bankControlStates.deleteBank"
              type="success"
              ghost
              @click="handleAddBank"
            >
              Change bank
            </n-button>
            <n-button
              :disabled="!bankControlStates.addBank || bankControlStates.deleteBank"
              type="success"
              ghost
              @click="handleCancelAddBank"
            >
              Cancel the change
            </n-button>
          </n-button-group>
          <n-button-group>
            <n-button :disabled="bankControlStates.deleteBank" type="error" ghost @click="handleDeleteBank">
              Delete bank
            </n-button>
            <n-button :disabled="!bankControlStates.deleteBank" type="error" ghost @click="handleCancelDeleteBank">
              Cancel deletion
            </n-button>
          </n-button-group>
        </div>
      </template>

      <template v-else-if="step === 3">
        <n-form-item path="data.bank.routingNumber" :label-props="{ for: 'routingNumber' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="Routing number"
              :is-required="true"
              demo-tooltip-text="Insert routing number (i.e. 222222226)"
              @on-demo-click="fillExampleRouting"
            />
          </template>
          <n-input
            v-model:value="formValue.data.bank.routingNumber"
            placeholder="123456789"
            :allow-input="onlyAllowNumber"
            maxlength="9"
            :input-props="{ id: 'routingNumber' }"
          />
        </n-form-item>

        <n-form-item path="data.bank.accountNumber" :label-props="{ for: 'accountNumber' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="Account number"
              :is-required="true"
              demo-tooltip-text="Insert account number (i.e. 1234567890)"
              @on-demo-click="fillExampleAccount"
            />
          </template>
          <n-input
            v-model:value="formValue.data.bank.accountNumber"
            placeholder="123456789"
            :allow-input="onlyAllowNumber"
            minlength="4"
            maxlength="17"
            :input-props="{ id: 'accountNumber' }"
          />
        </n-form-item>

        <n-form-item path="data.bank.accountType" :label-props="{ for: 'accountType' }" :show-require-mark="false">
          <template #label>
            <FormLabel
              label="Type"
              :is-required="true"
              demo-tooltip-text="Insert type (i.e. checking)"
              @on-demo-click="fillExampleType"
            />
          </template>
          <n-select
            v-model:value="formValue.data.bank.accountType"
            :options="bankTypeSelectOptions"
            filterable
            :input-props="{ id: 'accountType' }"
          />
        </n-form-item>
      </template>
    </Form>

    <template #action>
      <template v-if="isLastStep">
        <n-button type="primary" @click="save()">Save</n-button>
      </template>
      <template v-else>
        <n-button type="primary" @click="goNextStep()">Next</n-button>
        <n-button v-if="!isNotFirstStep" @click="closeModal">Cancel</n-button>
      </template>
      <template v-if="isSkipButtonVisible">
        <n-button type="primary" @click="skipStep()">Skip</n-button>
      </template>
      <template v-if="isNotFirstStep">
        <n-button @click="goPrevStep()">Back</n-button>
      </template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FormLabel from '../../base/components/form-label.vue';
import { ref, reactive, computed, watch } from 'vue';
import type { FormItemRule, FormRules } from 'naive-ui';
import type { CarrierFormDataDto } from '../../base/common/dtos/carrier-list.dto';
import { State } from '../../base/common/enums/state';
import { BankAccountType } from '../../base/common/dtos';
import type { SelectMixedOption } from 'naive-ui/es/select/src/interface';
import type { VueTelInputPhoneObject } from '../../base/common/interfaces/vue-tel-input';
import type Modal from '../../base/components/Modal.vue';
import type Form from '../../base/components//Form.vue';
import type {
  AddressControlStates,
  BankControlStates,
  CarriersFormModalOnSubmitPropsModel,
  CarriersFormModalPropsModel,
} from '../../base/common/models/contact-catalogs/carriers-form-modal-props.model';

const { data: $auth } = useAuth();

const modal = ref<InstanceType<typeof Modal> | null>(null);
const form = ref<InstanceType<typeof Form> | null>(null);

const openModal = () => {
  modal.value?.openModal();
};

const closeModal = () => {
  modal.value?.closeModal();

  resetForm();
};

defineExpose({ openModal, closeModal });

function onModalShowUpdate(value: boolean): void {
  if (!value) {
    step.value = minStep;
    resetForm();
  }
}

const props = defineProps<CarriersFormModalPropsModel>();
const propsReactive = reactive(props);

const emits = defineEmits<{
  (e: 'onSubmit', model: CarriersFormModalOnSubmitPropsModel): void;
}>();

const rules: FormRules = {
  data: {
    email: [
      {
        required: true,
        type: 'email',
        trigger: ['blur'],
        message: 'Email is required with valid format',
      },
      {
        required: true,
        type: 'email',
        trigger: ['input', 'blur'],
        validator(rule: FormItemRule, value: string) {
          if (value.trim() === $auth.value?.email) {
            return new Error('Email cannot be same as logged in user email');
          }
          return true;
        },
      },
    ],
    businessName: {
      required: false,
      trigger: ['input', 'blur'],
      max: 100,
      message: 'Business name cannot be longer than 100 characters',
    },
    address: {
      address1: {
        required: true,
        trigger: ['input', 'blur'],
        message: 'Address 1 is required',
        whitespace: true,
      },
      address2: {
        required: false,
        trigger: ['input', 'blur'],
      },
      city: {
        required: true,
        trigger: ['input', 'blur'],
        message: 'City is required',
        whitespace: true,
      },
      state: {
        required: true,
        trigger: ['input', 'blur'],
        message: 'State is required',
      },
      postalCode: {
        required: true,
        trigger: ['input', 'blur'],
        message: 'Postal code is required',
        whitespace: true,
      },
      phone: {
        required: false,
        trigger: ['input', 'blur'],
        validator: () => {
          return isPhoneValid.value;
        },
        message: 'Invalid phone number',
      },
    },
    bank: {
      routingNumber: {
        required: true,
        trigger: ['input', 'blur'],
        len: 9,
        message: 'A route number consisting of 9 digits is required',
      },
      accountNumber: {
        required: true,
        trigger: ['input', 'blur'],
        max: 17,
        min: 4,
        message: 'An account number consisting of 4-17 digits is required',
      },
      accountType: {
        required: true,
        trigger: ['input', 'blur'],
      },
      defaultCommission: {
        required: false,
        type: 'number',
        trigger: ['input', 'blur'],
      },
    },
  },
};

const minStep = 1;
const maxStep = 3;
const step = ref(minStep);
const skipButtonVisibilitySteps = [2, 3];
const isPhoneValid = ref(true);
const phoneValidationStatus = computed(() => (isPhoneValid.value ? 'success' : 'error'));
const phoneFeedback = computed(() => (isPhoneValid.value ? '' : 'Invalid phone number'));
const onlyAllowNumber = (value: string) => !value || /^\d+$/.test(value);

const initPersonalFormValues: Omit<CarrierFormDataDto, 'address' | 'bank'> = {
  email: '',
  businessName: null,
  defaultCommission: null,
};

const initAddressFormValues: CarrierFormDataDto['address'] = {
  address1: '',
  address2: null,
  city: '',
  state: State.AK,
  postalCode: '',
  phone: null,
};

const initBankFormValues: CarrierFormDataDto['bank'] = {
  routingNumber: '',
  accountNumber: '',
  accountType: BankAccountType.Checking,
};

const initAddressControlStates: AddressControlStates = {
  skipAddress: false,
};

const initBankControlStates: BankControlStates = {
  showControls: false,
  addBank: false,
  deleteBank: false,
  isBankExists: false,
  skipBank: false,
};

const formValue = ref<{ data: CarrierFormDataDto }>({
  data: {
    ...initPersonalFormValues,
    address: { ...initAddressFormValues },
    bank: { ...initBankFormValues },
  },
});

const stateSelectOptions: SelectMixedOption[] = Object.values(State).map((key) => ({
  label: key,
  value: key,
}));

const bankTypeSelectOptions: SelectMixedOption[] = Object.values(BankAccountType).map((key) => ({
  label: key,
  value: key,
}));

const bankControlStates = ref<BankControlStates>({ ...initBankControlStates });
const addressControlStates = ref<AddressControlStates>({ ...initAddressControlStates });

function fillExampleEmail() {
  formValue.value.data.email = 'carrier@email.com';
}
function fillExampleBusinessName() {
  formValue.value.data.businessName = 'AlphaTheta';
}
function fillExampleAddress1() {
  formValue.value.data.address.address1 = '123 Pineapple St';
}
function fillExampleCity() {
  formValue.value.data.address.city = 'Philadelphia';
}
function fillExampleState() {
  formValue.value.data.address.state = State.PA;
}
function fillExamplePostalCode() {
  formValue.value.data.address.postalCode = '19123';
}
function fillExamplePhone() {
  formValue.value.data.address.phone = '+12673212345';
}
function fillExampleRouting() {
  formValue.value.data.bank.routingNumber = '222222226';
}
function fillExampleAccount() {
  formValue.value.data.bank.accountNumber = '1234567890';
}
function fillExampleType() {
  formValue.value.data.bank.accountType = BankAccountType.Checking;
}

function handleAddBank(): void {
  bankControlStates.value.addBank = true;
  bankControlStates.value.showControls = false;
}
function handleCancelAddBank(): void {
  bankControlStates.value.addBank = false;
  resetBankFormValue();
}
function handleDeleteBank(): void {
  bankControlStates.value.deleteBank = true;
  bankControlStates.value.addBank = false;
  resetBankFormValue();
}
function handleCancelDeleteBank(): void {
  bankControlStates.value.deleteBank = false;
}

const setPhoneValidity = async (phoneObject: VueTelInputPhoneObject) => {
  isPhoneValid.value = phoneObject.valid ?? true;
};

async function goNextStep() {
  try {
    await partialValidation();

    if (step.value === maxStep && bankControlStates.value.addBank) {
      bankControlStates.value.showControls = true;
    } else {
      step.value++;
    }
  } catch {}
}

async function skipStep() {
  if (step.value === 2) {
    addressControlStates.value.skipAddress = true;
  } else if (step.value === 3) {
    bankControlStates.value.skipBank = true;

    if (bankControlStates.value.addBank) {
      bankControlStates.value.addBank = false;
    }
  }

  if (isLastStep.value) {
    await save(true);

    return;
  } else {
    step.value++;
  }
}

function goPrevStep() {
  if (
    !bankControlStates.value.showControls &&
    bankControlStates.value.isBankExists &&
    bankControlStates.value.addBank
  ) {
    bankControlStates.value.showControls = true;
    bankControlStates.value.addBank = false;

    resetBankFormValue();
  } else {
    step.value--;
  }
}

const isLastStep = computed(
  () =>
    (step.value === maxStep && bankControlStates.value.showControls) ||
    (step.value === maxStep && !bankControlStates.value.addBank),
);
const isNotFirstStep = computed(() => step.value > minStep);
const isSkipButtonVisible = computed(() => skipButtonVisibilitySteps.includes(step.value));

async function partialValidation() {
  await form.value?.formRef?.validate();
}

function emitSave(): void {
  emits('onSubmit', {
    ...formValue.value.data,
    skipAddress: addressControlStates.value.skipAddress,
    skipBank: bankControlStates.value.skipBank,
    deleteBank: bankControlStates.value.deleteBank,
  });

  resetForm();

  closeModal();
}

async function save(skipValidation?: boolean) {
  if (
    !bankControlStates.value.addBank &&
    !formValue.value.data.bank.routingNumber.length &&
    !formValue.value.data.bank.accountNumber.length
  ) {
    bankControlStates.value.skipBank = true;
  }

  if (skipValidation) {
    emitSave();
  } else {
    await form.value?.formRef?.validate(async (errors) => {
      if (!errors) {
        emitSave();
      }
    });
  }
}

function resetFormValue(): void {
  formValue.value.data = {
    ...initPersonalFormValues,
    address: { ...initAddressFormValues },
    bank: { ...initBankFormValues },
  };
}

function resetBankFormValue(): void {
  formValue.value.data.bank = { ...initBankFormValues };
}

function resetBankControlStates(): void {
  bankControlStates.value = { ...initBankControlStates };
}

function resetAddressControlStates(): void {
  addressControlStates.value = { ...initAddressControlStates };
}

function resetForm(): void {
  step.value = minStep;

  resetFormValue();
  resetBankFormValue();
  resetBankControlStates();
  resetAddressControlStates();
}

const onCommissionInputUpdate = (value: number | null) => {
  formValue.value.data.defaultCommission = value;
};

watch(
  () => propsReactive.carrier,
  () => {
    if (propsReactive.carrier) {
      const carrier = propsReactive.carrier;

      formValue.value.data.email = carrier.email;
      formValue.value.data.businessName = carrier.businessName;
      formValue.value.data.defaultCommission = carrier.commission;

      if (carrier.address) {
        formValue.value.data.address = { ...carrier.address };
      }

      bankControlStates.value.isBankExists = carrier.isBankExists;
      bankControlStates.value.showControls = carrier.isBankExists;
    }
  },
);
</script>

<style scoped lang="scss">
.sr-carrier-form-bank-control-header {
  text-align: center;
}

.sr-carrier-form-bank-control-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  margin: 36px 0 36px 0;
}

@media (min-width: 769px) {
  .sr-carrier-form-bank-control-actions {
    flex-direction: row;
    gap: 0;
    justify-content: space-between;
  }
}
</style>
